<template>
  <div class="pos-tab-nav">
    <div class="nav-logo" data-tauri-drag-region style="-webkit-app-region: drag;">
      <!-- POS sidebar lockup. wordmark hidden in this narrow rail; the
           logomark alone is enough at 22px. -->
      <StarvlyBrand size="sm" :wordmark="false" />
    </div>
    <div class="nav-tabs-container">
      <div
        v-for="item in filteredNavItems"
        :key="item.title"
        :class="['nav-tab', { 'active': isActive(item) }]"
        @click="navigateTo(item)"
      >
        <VBadge
          v-if="item.badgeContent"
          :color="item.badgeClass?.includes('warning') ? 'warning' : 'error'"
          :content="item.badgeContent"
          offset-x="-2"
          offset-y="2"
        >
          <div class="tab-icon">
            <VIcon :icon="item.icon.icon" size="22"/>
          </div>
        </VBadge>
        <div v-else class="tab-icon">
          <VIcon :icon="item.icon.icon" size="22"/>
        </div>
        <div class="tab-label">{{ item.title }}</div>
      </div>
    </div>

    <div class="nav-bottom">
      <VMenu v-model="showNotificationMenu" :close-on-content-click="false" location="end top" offset="8" width="320">
        <template #activator="{ props }">
          <div class="nav-tab icon-only" v-bind="props">
            <VBadge v-if="unreadCount > 0" :content="unreadCount" color="error" offset-x="-2" offset-y="2">
              <div class="tab-icon">
                <VIcon icon="mdi mdi-bell" size="22"/>
              </div>
            </VBadge>
            <div v-else class="tab-icon">
              <VIcon icon="mdi mdi-bell-outline" size="22"/>
            </div>
          </div>
        </template>
        <VCard>
          <VCardTitle class="d-flex align-center justify-space-between py-2 px-4">
            <span class="text-subtitle-1">Notifications</span>
            <VBtn v-if="unreadCount > 0" size="small" variant="text" @click="markAllRead">Mark all read</VBtn>
          </VCardTitle>
          <VDivider/>
          <VList v-if="notifications.length" class="overflow-y-auto" density="compact" max-height="300">
            <VListItem
              v-for="notification in notifications"
              :key="notification.id"
              :class="{ 'bg-primary-lighten-5': !notification.isSeen }"
              @click="handleNotificationClick(notification)"
            >
              <template #prepend>
                <VIcon :color="notification.color || 'primary'" :icon="notification.icon || 'mdi mdi-bell'" size="20"/>
              </template>
              <VListItemTitle class="text-body-2">{{ notification.title }}</VListItemTitle>
              <VListItemSubtitle class="text-caption">{{ notification.subtitle }}</VListItemSubtitle>
            </VListItem>
          </VList>
          <div v-else class="pa-4 text-center text-disabled">
            <VIcon class="mb-2" icon="mdi mdi-bell-off-outline" size="32"/>
            <div class="text-caption">No notifications</div>
          </div>
        </VCard>
      </VMenu>

      <VMenu location="end top" offset="8">
        <template #activator="{ props }">
          <div class="nav-tab icon-only" v-bind="props">
            <div class="tab-icon">
              <VIcon :icon="currentThemeIcon" size="22"/>
            </div>
          </div>
        </template>
        <VCard min-width="180">
          <VCardTitle class="text-subtitle-2 py-2 px-4">Theme</VCardTitle>
          <VDivider/>
          <VList density="compact" nav>
            <VListItem
              v-for="option in themeOptions"
              :key="option.name"
              :active="theme.global.name.value === option.name"
              @click="setTheme(option.name)"
            >
              <template #prepend>
                <div
                  :style="{ backgroundColor: option.color }"
                  class="theme-color-dot mr-3"
                />
              </template>
              <VListItemTitle class="text-body-2">{{ option.label }}</VListItemTitle>
            </VListItem>
          </VList>
        </VCard>
      </VMenu>

      <VMenu location="end top" offset="8">
        <template #activator="{ props }">
          <div class="nav-tab icon-only" v-bind="props">
            <div class="tab-icon">
              <VAvatar color="primary" size="28" variant="tonal">
                <span class="text-caption font-weight-bold">{{ userInitials }}</span>
              </VAvatar>
            </div>
          </div>
        </template>
        <VList density="compact" nav>
          <VListItem prepend-icon="mdi mdi-logout" title="Logout" @click="handleLogout"/>
        </VList>
      </VMenu>
    </div>
  </div>
</template>

<script setup>
import StarvlyBrand from '@shared/components/StarvlyBrand.vue'
import {computed} from 'vue'
import {useTheme} from 'vuetify'
import navItems from '@/navigation/horizontal'
import {themeOptions} from '@/plugins/vuetify/theme'
import {useReservationsStore} from '@/stores/reservations'
import {useNotificationStore} from '~/stores/notifications'
import {useOrderEvents} from '~/composables/useOrderEvents'
import {useI18n} from 'vue-i18n'

const {t} = useI18n()
const router = useRouter()
const route = useRoute()
const theme = useTheme()
const userStore = useUserStore()
const reservationsStore = useReservationsStore()
const notificationStore = useNotificationStore()
const {stopNotificationSound} = useOrderEvents()
const {isTabletMode} = useTabletMode()

const isDark = computed(() => theme.global.current.value.dark)
const currentThemeIcon = computed(() => {
  const currentTheme = theme.global.name.value
  const option = themeOptions.find(o => o.name === currentTheme)
  return option ? `mdi ${option.icon}` : 'mdi mdi-palette'
})
const showNotificationMenu = ref(false)

const notifications = computed(() => notificationStore.allNotifications)
const unreadCount = computed(() => notificationStore.allNotifications.filter(n => !n.isSeen).length)

const handleNotificationClick = (notification) => {
  if (!notification.isSeen) {
    notificationStore.markAsRead(notification.id)
  }
  stopNotificationSound()
  showNotificationMenu.value = false
  if (notification.link) {
    router.push(notification.link)
  }
}

const markAllRead = () => {
  notifications.value.forEach(n => {
    if (!n.isSeen) notificationStore.markAsRead(n.id)
  })
}

const filteredNavItems = computed(() => {
  let items = navItems

  if (isTabletMode.value) {
    items = items.filter(item => {
      const allowedRoutes = ['orders', 'order-type', 'tables', 'settings']
      const routeName = typeof item.to === 'object' ? item.to.name : item.to
      return allowedRoutes.includes(routeName)
    })
  } else {
    items = items.filter(item => {
      const routeName = typeof item.to === 'object' ? item.to.name : item.to
      return routeName !== 'kds'
    })
  }

  return items.map(item => {
    const routeName = typeof item.to === 'object' ? item.to.name : item.to
    const translatedItem = {
      ...item,
      title: t(item.title),
    }
    if (routeName === 'reservations' && reservationsStore.pendingCount > 0) {
      return {
        ...translatedItem,
        badgeContent: reservationsStore.pendingCount > 99 ? '99+' : String(reservationsStore.pendingCount),
        badgeClass: 'bg-warning',
      }
    }
    return translatedItem
  })
})

const userName = computed(() => {
  return userStore.user?.name || userStore.user?.email?.split('@')[0] || 'User'
})

const userInitials = computed(() => {
  const name = userName.value
  if (!name) return 'U'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

const isActive = (item) => {
  if (!item.to) return false
  const currentRoute = route.name
  const targetRoute = typeof item.to === 'object' ? item.to.name : item.to

  if (targetRoute === 'order-type' && currentRoute === 'new-order') return true
  if (targetRoute === 'new-order' && currentRoute === 'order-type') return true
  if (targetRoute === 'index' && currentRoute === 'index') return true

  return currentRoute === targetRoute
}

const navigateTo = (item) => {
  if (item.to) {
    router.push(item.to)
  }
}

const setTheme = (themeName) => {
  theme.global.name.value = themeName
  localStorage.setItem('pos-theme', themeName)
}

onMounted(() => {
  const savedTheme = localStorage.getItem('pos-theme')
  if (savedTheme && themeOptions.some(o => o.name === savedTheme)) {
    theme.global.name.value = savedTheme
  }
})

const handleLogout = async () => {
  await userStore.logout()
  router.push('/login')
}
</script>

<style scoped>
.pos-tab-nav {
  display: flex;
  flex-direction: column;
  width: 80px;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.nav-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  color: rgb(var(--v-theme-on-surface));
}

.logo-img {
  width: 43px;
  height: auto;
  filter: brightness(0) invert(1);
}

.v-theme--light .logo-img {
  filter: brightness(0) saturate(100%);
}

.nav-tabs-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 6px !important;
  overflow-y: auto;
}

.nav-tabs-container::-webkit-scrollbar {
  width: 0;
}

.nav-bottom {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 6px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.nav-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  min-height: 55px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
}

@media (min-width: 1200px) {
  .nav-tab {
    margin-top: 10px;
  }
}

.nav-tab:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}

.nav-tab.active {
  background: rgb(var(--v-theme-primary));
}

.nav-tab.active .tab-icon,
.nav-tab.active .tab-label {
  color: white;
}

.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.nav-tab:hover .tab-icon {
  color: rgb(var(--v-theme-primary));
}

.nav-tab.active .tab-icon {
  color: white;
}

.tab-label {
  font-size: calc(9px * var(--pos-font-scale, 1));
  font-weight: 500;
  text-align: center;
  line-height: 1.1;
  color: rgba(var(--v-theme-on-surface), 0.7);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-tab:hover .tab-label {
  color: rgb(var(--v-theme-primary));
}

.nav-tab.active .tab-label {
  color: white;
}

.nav-tab.icon-only {
  min-height: 40px;
  padding: 8px;
}

.nav-tab.icon-only .tab-icon {
  margin-bottom: 0;
}

.theme-color-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(var(--v-border-color), 0.3);
}
</style>
